import { ReactNode, useCallback, useState } from 'react';
import { Table } from '@tanstack/react-table';
import classNames from 'classnames/bind';
import { useIntl } from 'react-intl';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis, restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import {
  ValidationInfoFilledIcon,
  DownArrowIcon,
  UpArrowIcon,
  SettingsIcon,
  AdjustmentsIcon,
  FilterIcon,
} from '@entur/icons';
import { Tooltip } from '@entur/tooltip';
import { Checkbox } from '@entur/form';
import { IconButton } from '@entur/button';
import { isAllRowsChecked } from '../cell-renderers/parseCellValue';
import { type CleosTableFeaturesType } from '../tableFeatures';
import { LegacyColumnConfig, SortConfig } from '../types';
import styles from '../styles.module.scss';
import { FilterModalColumn } from '../hooks/useClientFilter';

const cx = classNames.bind(styles);

interface HeaderCellProps<TData extends object> {
  columnKey: string;
  meta: LegacyColumnConfig;
  table: Table<CleosTableFeaturesType, TData>;
  sortConfig?: SortConfig;
  isDraggable: boolean;
  clientFilter: boolean;
  onOpenFilterModal: (column: FilterModalColumn) => void;
  columnHasFilter: (columnKey: string) => boolean;
  isActiveItem?: boolean;
  showColumnKeys?: boolean;
  style?: React.CSSProperties;
}

const HeaderCell = <TData extends object>({
  columnKey,
  meta,
  table,
  sortConfig,
  isDraggable,
  clientFilter,
  onOpenFilterModal,
  columnHasFilter,
  isActiveItem,
  showColumnKeys = false,
  style: overrideStyle,
}: HeaderCellProps<TData>) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: columnKey,
    disabled: !isDraggable || !!meta.checkbox,
  });

  const intl = useIntl();
  const sortStyle = { transform: CSS.Transform.toString(transform), transition, ...overrideStyle };
  const isCheckbox = !!meta.checkbox;
  const isNumber = !!(meta.number || meta.amount || meta.format);
  const isIcon = !!(meta.icon || meta.checkbox);
  const hasFilter = columnHasFilter(columnKey);
  const isFilterable = clientFilter && !isCheckbox && meta.filterable !== false;

  const thClasses = cx({
    th: true,
    number: isNumber,
    icon: isIcon,
    truncate: meta.truncate,
    noWrap: meta.noWrap,
    active: isActiveItem,
  });

  const direction = sortConfig?.getSortDirection(columnKey) ?? null;

  // Single source for the header label, so the filter modal title always matches what the header shows
  const resolvedLabel = meta.headerKey ? intl.formatMessage({ id: meta.headerKey }) : meta.text;
  const displayLabel = showColumnKeys ? columnKey : resolvedLabel;

  const handleClick = () => {
    if (meta.sortable && sortConfig) {
      sortConfig.handleSorting(columnKey, direction === 'ASC' ? 'DESC' : 'ASC');
    } else if (isFilterable) {
      onOpenFilterModal({
        key: columnKey,
        label: displayLabel.replace(/\|/g, ''),
        config: meta,
      });
    }
  };

  const renderText = () => {
    if (displayLabel.includes('|')) {
      const parts = displayLabel.split('|');
      return (
        <span>
          <span className={cx({ noBreak: true, activeFilter: hasFilter })}>{parts[0]}</span>
          <span className={cx({ noBreak: true, activeFilter: hasFilter })}>{parts[1]}</span>
        </span>
      );
    }
    return <span className={cx({ headerLabel: true, activeFilter: hasFilter })}>{displayLabel}</span>;
  };

  const renderSelectAll = () => {
    if (!meta.checkbox?.selectAll || !meta.checkbox.onSelectAll) return null;
    const allRows = table.getFilteredRowModel().rows.map((r) => r.original);
    const enabledRows = allRows.filter((r) => !meta.checkbox!.isDisabled(r));
    return (
      <Checkbox
        data-testid="select-all-checkbox"
        className={styles.selectAllCheckbox}
        checked={isAllRowsChecked(enabledRows, meta.checkbox.isChecked)}
        onChange={(e) => meta.checkbox!.onSelectAll!(enabledRows, e.target.checked)}
        disabled={enabledRows.length === 0}
      />
    );
  };

  return (
    <th
      data-column={columnKey}
      className={cx(thClasses)}
      ref={setNodeRef}
      style={sortStyle}
      {...attributes}
      {...listeners}
    >
      <div onClick={handleClick} className={styles.sortWrapper} style={{ minWidth: meta.minWidth }}>
        {(meta.drawer || meta.tooltip) && (
          <Tooltip
            content={
              meta.tooltip
                ? (meta.tooltip.text ?? '')
                : intl.formatMessage(
                    { id: 'generalValues.drawerInfo' },
                    { data: meta.text.includes('|') ? meta.text.replace(/\|/g, '') : meta.text }
                  )
            }
            placement="left"
          >
            <span onClick={(e) => e.stopPropagation()}>
              <ValidationInfoFilledIcon className={styles.infoIcon} />
            </span>
          </Tooltip>
        )}
        {renderText()}
        {meta.sortable && sortConfig && direction === 'ASC' && <UpArrowIcon className={styles.sortIcon} size={12} />}
        {meta.sortable && sortConfig && direction === 'DESC' && <DownArrowIcon className={styles.sortIcon} size={12} />}
        {meta.sortable && sortConfig && !direction && <DownArrowIcon className={styles.sortIconHover} size={12} />}
        {renderSelectAll()}
        {!meta.sortable && isFilterable && <FilterIcon className={cx({ filterIcon: true, activeFilter: hasFilter })} />}
      </div>
    </th>
  );
};

interface TableHeaderProps<TData extends object> {
  table: Table<CleosTableFeaturesType, TData>;
  sortConfig?: SortConfig;
  expandable?: boolean;
  hasActions?: boolean;
  hasColumnSelector?: boolean;
  isDefault?: boolean;
  openColumnModal: () => void;
  clientFilter?: boolean;
  onOpenFilterModal: (column: FilterModalColumn) => void;
  columnHasFilter: (columnKey: string) => boolean;
  columnOrder: string[];
  onColumnOrderChange: (order: string[]) => void;
  rightColumnButton?: (ctx: { filterValues: Record<string, unknown[]>; columnOrder: string[] }) => ReactNode;
  filterValues?: Record<string, unknown[]>;
  showColumnKeys?: boolean;
}

export const TableHeader = <TData extends object>({
  table,
  sortConfig,
  expandable,
  hasActions,
  hasColumnSelector,
  isDefault,
  openColumnModal,
  clientFilter = false,
  onOpenFilterModal,
  columnHasFilter,
  columnOrder,
  onColumnOrderChange,
  rightColumnButton,
  filterValues,
  showColumnKeys = false,
}: TableHeaderProps<TData>) => {
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const visibleColumns = table.getVisibleLeafColumns();
  const isDraggable = hasColumnSelector ?? false;

  const customCollisionDetection = useCallback(
    (args: Parameters<typeof closestCenter>[0]) => {
      const cols = table.getVisibleLeafColumns();
      const collisions = closestCenter(args);
      if (!collisions?.length) return collisions;
      return collisions.filter((collision) => {
        const col = cols.find((c) => c.id === collision.id);
        return !col?.columnDef.meta?.checkbox;
      });
    },
    [table]
  );

  const handleDragStart = useCallback((event: { active: { id: string } }) => {
    setActiveItemId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: { active: { id: string }; over: { id: string } | null }) => {
      setActiveItemId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = columnOrder.indexOf(String(active.id));
      const newIdx = columnOrder.indexOf(String(over.id));
      if (oldIdx !== -1 && newIdx !== -1) {
        onColumnOrderChange(arrayMove(columnOrder, oldIdx, newIdx));
      }
    },
    [columnOrder, onColumnOrderChange]
  );

  const activeColMeta = activeItemId ? visibleColumns.find((c) => c.id === activeItemId)?.columnDef.meta : null;

  return (
    <thead className={styles.thead}>
      <DndContext
        modifiers={[restrictToFirstScrollableAncestor, restrictToHorizontalAxis]}
        sensors={sensors}
        collisionDetection={customCollisionDetection as never}
        onDragStart={handleDragStart as never}
        onDragEnd={handleDragEnd as never}
        accessibility={{ container: document.body }}
      >
        <SortableContext items={visibleColumns.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
          <tr>
            {expandable && <th />}

            {visibleColumns.map((column) => {
              const meta = column.columnDef.meta;
              if (!meta) return null;
              return (
                <HeaderCell
                  key={column.id}
                  columnKey={meta.columnKey}
                  meta={meta}
                  table={table}
                  sortConfig={sortConfig}
                  isDraggable={isDraggable}
                  clientFilter={clientFilter}
                  onOpenFilterModal={onOpenFilterModal}
                  columnHasFilter={columnHasFilter}
                  isActiveItem={activeItemId === column.id}
                  showColumnKeys={showColumnKeys}
                />
              );
            })}

            {(hasColumnSelector || hasActions || rightColumnButton) && (
              <th className={styles.actionsHeader}>
                <div className={styles.actions}>
                  {rightColumnButton?.({
                    filterValues: filterValues ?? {},
                    columnOrder: visibleColumns.filter((c) => !c.columnDef.meta?.checkbox).map((c) => c.id),
                  })}
                  {hasColumnSelector && (
                    <Tooltip placement="left" content="Valgte kolonner">
                      <IconButton className={styles.icon} onClick={openColumnModal} aria-label="Valgte kolonner">
                        {isDefault ? (
                          <SettingsIcon className={styles.icon} />
                        ) : (
                          <AdjustmentsIcon className={styles.icon} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </th>
            )}
          </tr>

          <DragOverlay>
            {activeColMeta ? (
              <HeaderCell
                key={activeItemId!}
                columnKey={activeItemId!}
                meta={activeColMeta}
                table={table}
                sortConfig={sortConfig}
                isDraggable={isDraggable}
                clientFilter={clientFilter}
                onOpenFilterModal={onOpenFilterModal}
                columnHasFilter={columnHasFilter}
                showColumnKeys={showColumnKeys}
                style={{ backgroundColor: 'var(--cleos-frame)' }}
              />
            ) : null}
          </DragOverlay>
        </SortableContext>
      </DndContext>
    </thead>
  );
};
